import React, { createContext, useContext, useCallback, useRef, useState, useEffect, type ReactNode } from 'react';
import { driver, type DriveStep, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './tour-overlay-styles.css';
import { type TourDefinition, MOBILE_BREAKPOINT } from './driver-config';
import { shouldShowTour, markTourCompleted, resetTour, initTourCache } from './tour-persistence';
import { useAuth } from '@/contexts/integrated-auth';

interface TourContextType {
  startTour: (definition: TourDefinition, force?: boolean) => void;
  shouldAutoShow: (definition: TourDefinition) => boolean;
  replayTour: (definition: TourDefinition) => void;
  isActive: boolean;
  activeTourId: string | null;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export function useTour() {
  const context = useContext(TourContext);
  if (!context) {
    return {
      startTour: () => {},
      shouldAutoShow: () => false,
      replayTour: () => {},
      isActive: false,
      activeTourId: null,
    };
  }
  return context;
}

export function TourProvider({ children }: { children: ReactNode }) {
  const driverRef = useRef<Driver | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [activeTourId, setActiveTourId] = useState<string | null>(null);
  const { user } = useAuth();

  // Seed tour cache from server-side user data when user loads
  const tourCacheInitialized = useRef(false);
  useEffect(() => {
    if (user && !tourCacheInitialized.current) {
      initTourCache(user.completedTours as any);
      tourCacheInitialized.current = true;
    }
    if (!user) {
      tourCacheInitialized.current = false;
    }
  }, [user]);

  const startTour = useCallback((definition: TourDefinition, force = false) => {
    if (driverRef.current?.isActive()) return;
    if (!force && !shouldShowTour(definition.id, definition.version)) return;

    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    const filteredSteps = definition.steps.filter((step) => {
      if (!step.platform || step.platform === 'all') return true;
      return step.platform === (isMobile ? 'mobile' : 'desktop');
    });

    if (filteredSteps.length === 0) return;

    const driverSteps: DriveStep[] = filteredSteps.map((step) => {
      const driverStep: DriveStep = {
        popover: {
          title: step.popover.title,
          description: step.popover.description,
          side: step.popover.side || (isMobile ? 'bottom' : 'right'),
          align: step.popover.align || 'center',
        },
      };

      // If step targets a DOM element, resolve the first VISIBLE match.
      // Responsive layouts (sm:hidden / hidden sm:flex) can leave duplicate
      // data-tour elements in the DOM where the first match is hidden,
      // causing driver.js to anchor popovers at (0,0).
      if (step.element) {
        let resolvedElement: string | HTMLElement = step.element;
        const allMatches = document.querySelectorAll(step.element);
        for (const el of allMatches) {
          const rect = (el as HTMLElement).getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            resolvedElement = el as HTMLElement;
            break;
          }
        }
        driverStep.element = resolvedElement;
      }

      return driverStep;
    });

    const driverInstance = driver({
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      steps: driverSteps,
      overlayColor: 'rgba(0, 0, 0, 0.6)',
      popoverClass: 'yapzap-tour-popover',
      stagePadding: 8,
      stageRadius: 12,
      onDestroyStarted: () => {
        markTourCompleted(definition.id, definition.version);
        driverInstance.destroy();
      },
      onDestroyed: () => {
        setIsActive(false);
        setActiveTourId(null);
        driverRef.current = null;
      },
    });

    driverRef.current = driverInstance;
    setIsActive(true);
    setActiveTourId(definition.id);
    driverInstance.drive();
  }, []);

  const shouldAutoShowFn = useCallback((definition: TourDefinition) => {
    if (definition.trigger === 'manual-only') return false;
    return shouldShowTour(definition.id, definition.version);
  }, []);

  const replayTourFn = useCallback((definition: TourDefinition) => {
    resetTour(definition.id);
    startTour(definition, true);
  }, [startTour]);

  return (
    <TourContext.Provider
      value={{
        startTour,
        shouldAutoShow: shouldAutoShowFn,
        replayTour: replayTourFn,
        isActive,
        activeTourId,
      }}
    >
      {children}
    </TourContext.Provider>
  );
}
